import {useCallback, useState} from 'react';
import {CApi as userGwApi} from '@services/api';
import {
    AddFormKind,
    ClientPreference,
    ClientPreferenceType,
    getPreferenceTypeByFormKind
} from '@features/profile/types/FavoritePriceLists';

const preferenceListItems = async (
    login: string,
    kind: AddFormKind
): Promise<ClientPreference[]> => {
    const type = getPreferenceTypeByFormKind(kind);
    const response = await userGwApi().client.preferenceList(login, {type});
    return (response.data?.items ?? []) as ClientPreference[];
};

async function preferenceListByType(
    login: string,
    type: ClientPreferenceType
): Promise<ClientPreference[]> {
    const response = await userGwApi().client.preferenceList(login, {type});
    return (response.data?.items ?? []) as ClientPreference[];
}

type AddPriceListParams = {kind: AddFormKind; value: string; comment: string};

export const usePriceListPreferences = (login: string | undefined) => {
    const [hasUserFavoritePriceLists, setHasUserFavoritePriceLists] = useState(false);
    const [hasUserBannedPriceLists, setHasUserBannedPriceLists] = useState(false);
    const [isPriceListPreferencesLoading, setIsPriceListPreferencesLoading] = useState(false);

    const refreshPreferenceLists = useCallback(async () => {
        if (!login) {
            setHasUserFavoritePriceLists(false);
            setHasUserBannedPriceLists(false);
            setIsPriceListPreferencesLoading(false);
            return;
        }

        setIsPriceListPreferencesLoading(true);
        try {
            const [favoriteItems, bannedItems] = await Promise.all([
                preferenceListByType(login, 'favorite_price_list'),
                preferenceListByType(login, 'banned_price_list')
            ]);
            setHasUserFavoritePriceLists(favoriteItems.length > 0);
            setHasUserBannedPriceLists(bannedItems.length > 0);
        } catch {
            setHasUserFavoritePriceLists(false);
            setHasUserBannedPriceLists(false);
        } finally {
            setIsPriceListPreferencesLoading(false);
        }
    }, [login]);

    const addPreference = useCallback(
        async (data: AddPriceListParams): Promise<ClientPreference | undefined> => {
            if (!login) {
                return undefined;
            }

            const type = getPreferenceTypeByFormKind(data.kind);
            const response = await userGwApi().client.preferenceCreate(login, {
                body: {
                    type,
                    value: data.value,
                    comment: data.comment
                }
            } as any);

            return response.data as ClientPreference | undefined;
        },
        [login]
    );

    const removePreference = useCallback(
        async (rowId: string): Promise<void> => {
            if (!login) {
                return;
            }

            await userGwApi().client.preferenceDelete(login, rowId);
        },
        [login]
    );

    const findPreferenceIdByPriceList = useCallback(
        async (kind: AddFormKind, priceList: string): Promise<string | undefined> => {
            if (!login) {
                return undefined;
            }

            const items = await preferenceListItems(login, kind);
            return items.find((p) => p.value === priceList)?.id;
        },
        [login]
    );

    return {
        addPreference,
        removePreference,
        findPreferenceIdByPriceList,
        hasUserFavoritePriceLists,
        hasUserBannedPriceLists,
        isPriceListPreferencesLoading,
        refreshPreferenceLists
    };
};
